<template>
  <div class="set-username-container">
    <form @submit.prevent="handleSetUsername" class="set-username-form">
      <h2>Elige Tu Nombre de Usuario</h2>
      <div class="form-group">
        <label for="username">Nombre de Usuario</label>
        <input
          type="text"
          id="username"
          v-model="username"
          @input="validateUsername"
          required
          placeholder="Elige un nombre de usuario"
          autocomplete="username"
          :disabled="loading || checking"
          :class="{ 'input-error': validationError }"
        />
        <p v-if="validationError" class="validation-error">{{ validationError }}</p>
        <p v-if="checking" class="checking">Verificando disponibilidad del nombre de usuario...</p>
        <div class="validation-rules">
          <p class="rule-text">El nombre de usuario debe:</p>
          <ul class="rules-list">
            <li
              :class="{
                'rule-valid': validationRules.length,
                'rule-invalid': !validationRules.length,
              }"
            >
              Tener entre 3 y 20 caracteres
            </li>
            <li
              :class="{
                'rule-valid': validationRules.characters,
                'rule-invalid': !validationRules.characters,
              }"
            >
              Solo contener letras, números, guiones "-" y guiones bajos "_"
            </li>
            <li
              :class="{
                'rule-valid': validationRules.format,
                'rule-invalid': !validationRules.format,
              }"
            >
              Comenzar y terminar con una letra o número
            </li>
            <li
              :class="{
                'rule-valid': validationRules.noSpaces,
                'rule-invalid': !validationRules.noSpaces,
              }"
            >
              No contener espacios
            </li>
          </ul>
        </div>
      </div>
      <button type="submit" :disabled="loading || checking || !isUsernameValid">
        {{ loading ? 'Estableciendo nombre de usuario...' : 'Establecer Nombre de Usuario' }}
      </button>
      <p v-if="error" class="error">{{ error }}</p>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { authService } from '../services/auth'
import { config } from '../config'

const router = useRouter()
const username = ref('')
const loading = ref(false)
const error = ref('')
const checking = ref(false)
const validationError = ref('')
const API_URL = config.getBackendUrl()

// Validation rules tracking
const validationRules = ref({
  length: false,
  characters: false,
  format: false,
  noSpaces: false,
})

const isUsernameValid = computed(() => {
  return Object.values(validationRules.value).every((rule) => rule) && username.value.trim() !== ''
})

const validateUsername = () => {
  const usernameValue = username.value
  validationError.value = ''

  // Reset validation rules
  validationRules.value = {
    length: false,
    characters: false,
    format: false,
    noSpaces: false,
  }

  if (!usernameValue) {
    return
  }

  // Length validation (3-20 characters)
  if (usernameValue.length >= 3 && usernameValue.length <= 20) {
    validationRules.value.length = true
  } else {
    validationError.value = 'El nombre de usuario debe tener entre 3 y 20 caracteres'
    return
  }

  // Character validation (only letters, numbers, hyphens, underscores)
  const validCharacters = /^[a-zA-Z0-9_-]+$/
  if (validCharacters.test(usernameValue)) {
    validationRules.value.characters = true
  } else {
    validationError.value = 'Solo se permiten letras, números, guiones (-) y guiones bajos (_)'
    return
  }

  // No spaces validation
  if (!usernameValue.includes(' ')) {
    validationRules.value.noSpaces = true
  } else {
    validationError.value = 'El nombre de usuario no puede contener espacios'
    return
  }

  // Format validation (must start and end with alphanumeric)
  const formatPattern = /^[a-zA-Z0-9].*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/
  if (formatPattern.test(usernameValue)) {
    validationRules.value.format = true
  } else {
    validationError.value = 'El nombre de usuario debe comenzar y terminar con una letra o número'
    return
  }

  // Additional validations
  if (
    usernameValue === usernameValue.toLowerCase() ||
    usernameValue === usernameValue.toUpperCase()
  ) {
    // Allow both cases, but could add mixed case requirement if needed
  }

  // Check for consecutive special characters
  if (
    usernameValue.includes('--') ||
    usernameValue.includes('__') ||
    usernameValue.includes('-_') ||
    usernameValue.includes('_-')
  ) {
    validationError.value = 'No se permiten caracteres especiales consecutivos'
    return
  }
}

const checkUsernameAvailability = async (username: string): Promise<boolean> => {
  try {
    checking.value = true
    const response = await fetch(
      `${API_URL}/auth/username-is-available/${encodeURIComponent(username)}`,
    )
    const data = await response.json()
    console.log('data: ', data)
    return data
  } catch (err) {
    console.error('Error checking username:', err)
    return false
  } finally {
    checking.value = false
  }
}

const handleSetUsername = async () => {
  try {
    loading.value = true
    error.value = ''

    // Trim whitespace
    username.value = username.value.trim()

    // Validate username format first
    validateUsername()
    if (!isUsernameValid.value) {
      error.value = validationError.value || 'Por favor, ingresa un nombre de usuario válido'
      return
    }

    // Check if username is available
    const isAvailable = await checkUsernameAvailability(username.value)
    if (!isAvailable) {
      error.value = 'Este nombre de usuario ya está en uso. Por favor, elige otro.'
      return
    }

    await authService.setUsername(username.value)
    router.push('/daily')
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : 'Ocurrió un error al establecer el nombre de usuario'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.set-username-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.set-username-form {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.instructions {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #666;
}

.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.input-error {
  border-color: #dc3545 !important;
}

.validation-rules {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.rule-text {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
}

.rules-list {
  margin: 0;
  padding-left: 1.2rem;
  list-style: none;
}

.rules-list li {
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  position: relative;
}

.rules-list li::before {
  content: '●';
  position: absolute;
  left: -1rem;
}

.rule-valid {
  color: #28a745;
}

.rule-valid::before {
  content: '✓';
  color: #28a745;
  font-weight: bold;
}

.rule-invalid {
  color: #6c757d;
}

.rule-invalid::before {
  color: #6c757d;
}

button {
  width: 100%;
  padding: 0.75rem;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

button:hover:not(:disabled) {
  background-color: #45a049;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error {
  color: #dc3545;
  margin-top: 1rem;
  text-align: center;
}

.validation-error {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.checking {
  color: #666;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  font-style: italic;
}
</style>
